<template>
  <van-popup :show="show" onUpdate:show="updateShow" position="center" round
    :style="{ background: 'transparent', padding: '0' }" :close-on-click-overlay="closeOnClickOverlay"
    @closed="handleClosed" destroy-on-close="">
    <div class="video-invite-modal">
      <!-- Close button -->
      <button v-if="showClose" class="close-btn" @click="handleClose" aria-label="Close">
        <img src="@/assets/images/icons/close2.svg" alt="Close Button" />
      </button>

      <!-- Avatar -->
      <div class="avatar-container">
        <img v-if="inviter?.avatar" :src="inviter?.avatar" alt="Avatar" class="avatar" />
      </div>

      <div class="background-container">
        <!-- Name -->
        <h3 class="inviter-name">{{ inviter?.name }}</h3>

        <!-- Country Badge -->
        <div v-if="inviter?.country" class="country-badge">
          <span class="country-flag">{{ inviter.country.flag }}</span>
          <span class="country-name">{{ inviter.country.name }}</span>
        </div>

        <!-- Invitation Text -->
        <p class="invitation-text">{{ displayInvitationText }}</p>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <!-- Reject Button -->
          <button class="action-btn reject-btn" @click="handleReject">
            <img src="@/assets/images/icons/phone-hang-up.svg" alt="Reject" />

            <!-- Cancel Timer -->
            <p v-if="showTimer" class="cancel-timer">
              {{ t('videoCall.invite.cancel', { seconds: remaining }) }}
            </p>
          </button>

          <!-- Accept Button -->
          <button class="action-btn accept-btn" @click="handleAccept">
            <img src="@/assets/images/icons/phone-call.svg" alt="Accept" />

            <!-- Free Badge -->
            <div v-if="videoCostInfo?.showFree && videoCostInfo?.freeIcon" class="free-badge">
              <img :src="videoCostInfo.freeIcon" alt="Free" class="free-icon" />
            </div>
          </button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { watch, onUnmounted, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCountdown } from '@vueuse/core'
import { useUserStore } from '@/stores/user'
import { getAnchorStatus } from '@/api/user'
import { getVideoCostInfo } from '@/utils'

const { t } = useI18n()
const userStore = useUserStore()

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inviter: {
    type: Object,
    default: () => ({
      name: '',
      avatar: '',
      country: {
        flag: '',
        name: '',
      },
    }),
  },
  invitationText: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  closeOnClickOverlay: {
    type: Boolean,
    default: false,
  },
  autoCloseSeconds: {
    type: Number,
    default: 30,
  },
  showTimer: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:show', 'close', 'closed', 'accept', 'reject', 'timeout'])

// 免费标签状态
const videoCostInfo = ref()

// 获取用户状态并计算免费标签
const fetchAnchorStatusAndCalculateFree = async () => {
  if (!props.inviter?.id) return

  try {
    const anchorStatusData = await getAnchorStatus({
      target_ids: [props.inviter.id],
      action: getAnchorStatus.ACTION.ONLY_CHECK, // 只查询，不上报
    }, {
      hideLoading: true,
      hideError: true,
    })

    const anchorStatus = anchorStatusData?.[0] || null

    // 使用 getVideoCostInfo 计算是否显示免费标签
    videoCostInfo.value = getVideoCostInfo({
      memberAsset: userStore.memberAsset,
      price: anchorStatus?.price,
      anchorStatus,
    })
  } catch (error) {
    console.error('[VideoInviteModal] Failed to fetch anchor status:', error)
  }
}

// 使用 vueuse 的 useCountdown
const { remaining, start, stop } = useCountdown(props.autoCloseSeconds)

// Computed properties for i18n
const displayInvitationText = computed(() => {
  return props.invitationText || t('videoCall.invite.text')
})

// Watch props.show to sync with visible
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      // 重置并启动倒计时
      start()
      // 异步获取状态并计算免费标签
      fetchAnchorStatusAndCalculateFree()
    } else {
      // 暂停倒计时
      stop()
      // 重置免费标签状态
      videoCostInfo.value = null
    }
  },
  { immediate: true }
)

// 监听倒计时结束
watch(remaining, (val) => {
  if (val <= 0 && props.show) {
    emit('timeout')
  }
})

const updateShow = (val) => {
  emit('update:show', val)
}

function handleClose() {
  stop()
  updateShow(false)
  emit('close')
}

function handleClosed() {
  stop()
  // updateShow(false)
  emit('closed')
}

function handleAccept() {
  // stop()
  // updateShow(false)
  emit('accept')
}

function handleReject() {
  stop()
  // updateShow(false)
  emit('reject')
}

onUnmounted(() => {
  stop()
})
</script>

<style scoped lang="less">
.video-invite-modal {
  position: relative;
  overflow: visible;
  width: 291px;

  // Close button - 在弹窗右上角
  .close-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    z-index: 10;

    img {
      width: 100%;
      height: 100%;
    }
  }

  // Avatar - 在弹窗顶部向外突出
  .avatar-container {
    width: 72px;
    height: 72px;
    margin: 0 auto;
    border-radius: 50%;
    overflow: hidden;
    border: 4px solid #ffffff;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 10;

    .avatar {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .background-container {
    margin-top: -36px;
    background: linear-gradient(180deg, #f1e0ff 0%, #ffffff 56px);
    padding: 40px 24px 44px;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    // Name
    .inviter-name {
      width: 80%;
      margin-bottom: 4px;
      color: #29244c;
      text-align: center;
      font-family: 'PingFang SC', sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      .ellipsis();
    }

    // Country Badge
    .country-badge {
      margin-bottom: 12px;
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 0 6px;
      height: 14px;
      background: #ebf1ff;
      border-radius: 21px;

      .country-flag {
        font-family: 'PingFang TC', sans-serif;
        font-size: 13px;
        line-height: 14px;
      }

      .country-name {
        font-family: 'PingFang SC', sans-serif;
        font-size: 10px;
        color: #494b64;
        line-height: 14px;
      }
    }

    // Invitation Text
    .invitation-text {
      margin-bottom: 14px;
      color: #29244c;
      text-align: center;
      font-family: 'PingFang SC', sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    // Action Buttons
    .action-buttons {
      display: flex;
      justify-content: center;
      gap: 59px;

      .action-btn {
        width: 48px;
        height: 48px;
        position: relative;
        cursor: pointer;

        >img {
          width: 100%;
          height: 100%;
        }

        &.reject-btn {

          // 视觉上和接听按钮保持一致
          >img {
            width: 92%;
            height: 92%;
          }
        }

        // Free Badge
        .free-badge {
          position: absolute;
          top: -7px;
          right: -3px;

          .free-icon {
            height: 24px;
            width: 24px;
            object-fit: contain;
          }
        }

        // Cancel Timer
        .cancel-timer {
          .absolute-center-x();
          top: calc(48px + 7px);
          color: #cccdd8;
          text-align: center;
          font-family: 'PingFang SC', sans-serif;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
